/**
 * Frontend Token Validation Utilities
 * Prevents JWT expired errors by validating tokens before API calls
 */

import { getAuthToken } from './localStorage';

import { safeStorage } from './safeStorage';
/**
 * Check if a JWT token is expired
 * @param {string} token - JWT token to validate
 * @returns {boolean} - True if token is expired or invalid
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    // Decode JWT payload (middle part of token)
    const payload = JSON.parse(atob(token.split('.')[1]));
    
    // Check if token has expiry time
    if (!payload.exp) return true;
    
    // Convert expiry to milliseconds and compare with current time
    const expiryTime = payload.exp * 1000;
    const currentTime = Date.now();
    
    // Add 30 second buffer to prevent edge cases
    const bufferTime = 30 * 1000;
    
    return currentTime >= (expiryTime - bufferTime);
  } catch (error) {
    console.warn('Token validation failed:', error.message);
    return true; // Treat invalid tokens as expired
  }
};

/**
 * Check if a JWT token is expiring soon (within 5 minutes)
 * @param {string} token - JWT token to validate
 * @returns {boolean} - True if token expires within 5 minutes
 */
export const isTokenExpiringSoon = (token) => {
  if (!token) return true;
  
  try {
    // Decode JWT payload (middle part of token)
    const payload = JSON.parse(atob(token.split('.')[1]));
    
    // Check if token has expiry time
    if (!payload.exp) return true;
    
    // Convert expiry to milliseconds and compare with current time
    const expiryTime = payload.exp * 1000;
    const currentTime = Date.now();
    
    // Check if expires within 5 minutes (300 seconds)
    const warningThreshold = 5 * 60 * 1000; // 5 minutes in milliseconds
    
    return currentTime >= (expiryTime - warningThreshold);
  } catch (error) {
    console.warn('Token expiry check failed:', error.message);
    return true; // Treat invalid tokens as expiring soon
  }
};

/**
 * Get valid token from localStorage with expiry validation
 * @returns {string|null} - Valid token or null if expired/missing
 */
export const getValidToken = () => {
  const token = getAuthToken();
  
  if (!token) {
    console.log('🔍 No token found in localStorage');
    return null;
  }
  
  if (isTokenExpired(token)) {
    console.log('⚠️ Token expired, clearing from localStorage');
    safeStorage.removeItem('evv_token');
    safeStorage.removeItem('evv_user');
    safeStorage.removeItem('evv_organization');
    return null;
  }
  
  return token;
};

/**
 * Clear all authentication data from localStorage
 */
export const clearAuthData = () => {
  console.log('🧹 Clearing all authentication data');
  
  const authKeys = [
    'evv_token',
    'evv_user',
    // AuthContext restores the signed-in user from this on the next page load;
    // left behind, it acts signed in with no token to send.
    'evv_user_cache',
    'evv_organization',
    'userProfile',
    'organizationData',
    'authToken',
    'jwt_token',
    'access_token'
  ];
  
  authKeys.forEach(key => {
    safeStorage.removeItem(key);
  });
};

/**
 * Check if user is authenticated with valid token
 * @returns {boolean} - True if user has valid authentication
 */
export const isAuthenticated = () => {
  return getValidToken() !== null;
};

/**
 * Get token expiry information for debugging and session management
 * @param {string} token - JWT token to analyze
 * @returns {object} - Token expiry information
 */
export const getTokenInfo = (token = null) => {
  const tokenToCheck = token || getAuthToken();
  
  if (!tokenToCheck) {
    return { exists: false, expired: true, info: 'No token found' };
  }
  
  try {
    const payload = JSON.parse(atob(tokenToCheck.split('.')[1]));
    const expiryTime = payload.exp * 1000;
    const currentTime = Date.now();
    const timeUntilExpiry = Math.max(0, Math.floor((expiryTime - currentTime) / 1000));
    
    return {
      exists: true,
      expired: timeUntilExpiry <= 0,
      expiresAt: new Date(expiryTime).toISOString(),
      timeUntilExpiry: timeUntilExpiry,
      userId: payload.userId,
      email: payload.email,
      role: payload.role,
      organizationId: payload.organizationId
    };
  } catch (error) {
    return { exists: true, expired: true, info: 'Invalid token format' };
  }
};

/**
 * Attempt to refresh the authentication token
 * @returns {Promise<boolean>} - Success status
 */
export const refreshAuthToken = async () => {
  try {
    const currentToken = getAuthToken();
    if (!currentToken) {
      console.log('🔍 No token available for refresh');
      return false;
    }

    // Import api here to avoid circular dependency
    const { createVersionedApi } = await import('./versionedApi');
    const apiClient = createVersionedApi('v1');
    
    const response = await apiClient.post('/users/refresh-token');
    
    if (response.data && response.data.token) {
      try { safeStorage.setItem('evv_token', response.data.token); } catch {}

      if (response.data.user) {
        try { safeStorage.setItem('evv_user', JSON.stringify(response.data.user)); } catch {}
        if (response.data.user.organization) {
          try { safeStorage.setItem('evv_organization', JSON.stringify(response.data.user.organization)); } catch {}
        }
      }

      console.log('✅ Token refreshed successfully');
      return true;
    }
    
    return false;
  } catch (refreshError) {
    console.warn('❌ Token refresh failed:', refreshError.message);
    console.warn('❌ Error details:', refreshError.response?.data || refreshError);
    
    // DON'T clear auth data on refresh failure
    // The token might still be valid - let SessionMonitor decide when to logout
    return false;
  }
};

/**
 * Enhanced token validation with automatic refresh attempt
 * @returns {Promise<string|null>} - Valid token or null
 */
export const getValidTokenWithRefresh = async () => {
  const token = getAuthToken();
  
  if (!token) {
    console.log('🔍 No token found in localStorage');
    return null;
  }
  
  const tokenInfo = getTokenInfo(token);
  
  if (tokenInfo.expired) {
    console.log('⚠️ Token expired, attempting refresh...');
    
    const refreshSuccess = await refreshAuthToken();
    if (refreshSuccess) {
      return getAuthToken();
    }
    
    return null;
  }
  
  // Check if token is close to expiry (within 10 minutes) and proactively refresh
  if (tokenInfo.timeUntilExpiry <= 600) { // 10 minutes
    console.log('🔄 Token expiring soon, proactively refreshing...');
    
    const refreshSuccess = await refreshAuthToken();
    if (refreshSuccess) {
      return getAuthToken();
    }
    
    // If refresh fails but token is still valid, return it
    return token;
  }
  
  return token;
};

/**
 * Public interface for token refresh (used by SessionTest)
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const refreshToken = async () => {
  try {
    const success = await refreshAuthToken();
    return {
      success,
      message: success ? 'Token refreshed successfully' : 'Token refresh failed'
    };
  } catch (error) {
    return {
      success: false,
      message: `Token refresh error: ${error.message}`
    };
  }
};
